import type { ConnectionFileInfo } from '../types/connection-files.types';

/**
 * Path handling for a remote file picker.
 *
 * Kept apart from the component because every one of these is the sort of thing
 * that is obvious until a trailing slash, a root directory or a name containing
 * a dot walks in.
 */

/** Absolute path of *name* inside *parent*, with no doubled separators. */
export function joinPath(parent: string, name: string): string {
  const base = parent.replace(/\/+$/, '');
  return `${base}/${name}`;
}

/** Parent of *path*; the root is its own parent, so "up" at the top is a no-op. */
export function parentPath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  const cut = trimmed.lastIndexOf('/');
  return cut <= 0 ? '/' : trimmed.slice(0, cut);
}

export interface Crumb {
  /** What to show — the folder's own name, or "/" for the root. */
  label: string;
  /** Absolute path to navigate to when clicked. */
  path: string;
}

/**
 * Breadcrumb for *path*, root first.
 *
 * Every segment is clickable, which is what makes "go up two levels" one click
 * instead of two — the reason a text box holding the whole path is a poor
 * navigator even though it is a fine display.
 */
export function breadcrumbs(path: string): Crumb[] {
  const crumbs: Crumb[] = [{ label: '/', path: '/' }];
  const parts = path.split('/').filter(Boolean);
  let current = '';
  for (const part of parts) {
    current = `${current}/${part}`;
    crumbs.push({ label: part, path: current });
  }
  return crumbs;
}

/**
 * Folders first, then files, each newest-first.
 *
 * These listings are dated exports, so the file someone wants is almost always
 * the most recent one; alphabetical order buries it in the middle of a year of
 * deliveries. Entries with no timestamp sort by name so the order stays stable
 * rather than depending on the server's enumeration.
 */
export function sortEntries(entries: ConnectionFileInfo[]): ConnectionFileInfo[] {
  return [...entries].sort((a, b) => {
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    if (a.modifiedAt && b.modifiedAt) return b.modifiedAt.localeCompare(a.modifiedAt);
    if (a.modifiedAt) return -1;
    if (b.modifiedAt) return 1;
    return a.name.localeCompare(b.name);
  });
}

/** Case-insensitive substring match on the entry name. */
export function filterEntries(entries: ConnectionFileInfo[], term: string): ConnectionFileInfo[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return entries;
  return entries.filter((entry) => entry.name.toLowerCase().includes(needle));
}

/**
 * The glob sent to the server when searching sub-folders.
 *
 * A recursive listing is the expensive call, so it only happens on demand, and
 * the term is wrapped rather than used raw: an operator types `product_perf`,
 * not `*product_perf*`, and a bare term would match nothing.
 */
export function searchGlob(term: string): string {
  const trimmed = term.trim();
  if (!trimmed) return '';
  return trimmed.includes('*') ? trimmed : `*${trimmed}*`;
}

/** Human-readable size for a listing row. */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes.toString()} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024).toString()} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
